'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { useLanguage } from '@/contexts/LanguageContext';
import { translations } from '@/lib/translations';

interface ClientSuccess {
  id: number;
  name: string;
  amount: number;
  investAmount: number;
  isPending?: boolean;
}

const clientSuccesses: ClientSuccess[] = [
  { id: 51, name: 'Jose', amount: 8000, investAmount: 150, isPending: true },
  { id: 1, name: 'Sofía', amount: 8000, investAmount: 150 },
  { id: 2, name: 'Mateo', amount: 10000, investAmount: 200 },
  { id: 3, name: 'Valentina', amount: 15000, investAmount: 300 },
  { id: 4, name: 'Santiago', amount: 18000, investAmount: 400 },
  { id: 5, name: 'Camila', amount: 25000, investAmount: 500 },
  { id: 6, name: 'Diego', amount: 8000, investAmount: 150 },
  { id: 7, name: 'Lucía', amount: 10000, investAmount: 200 },
  { id: 8, name: 'Sebastián', amount: 15000, investAmount: 300 },
  { id: 9, name: 'Isabella', amount: 18000, investAmount: 400 },
  { id: 10, name: 'Alejandro', amount: 25000, investAmount: 500 },
  { id: 11, name: 'Daniela', amount: 8000, investAmount: 150 },
  { id: 12, name: 'Nicolás', amount: 10000, investAmount: 200 },
  { id: 13, name: 'Mariana', amount: 15000, investAmount: 300 },
  { id: 14, name: 'Gabriel', amount: 18000, investAmount: 400 },
  { id: 15, name: 'Elena', amount: 25000, investAmount: 500 },
  { id: 16, name: 'Daniel', amount: 8000, investAmount: 150 },
  { id: 17, name: 'Gabriela', amount: 10000, investAmount: 200 },
  { id: 18, name: 'David', amount: 15000, investAmount: 300 },
  { id: 19, name: 'Natalia', amount: 18000, investAmount: 400 },
  { id: 20, name: 'Andrés', amount: 25000, investAmount: 500 },
  { id: 21, name: 'Renata', amount: 8000, investAmount: 150 },
  { id: 22, name: 'Emilio', amount: 10000, investAmount: 200 },
  { id: 23, name: 'Carmen', amount: 15000, investAmount: 300 },
  { id: 24, name: 'Leonardo', amount: 18000, investAmount: 400 },
  { id: 25, name: 'Victoria', amount: 25000, investAmount: 500 },
  { id: 26, name: 'Carlos', amount: 8000, investAmount: 150 },
  { id: 27, name: 'Adriana', amount: 10000, investAmount: 200 },
  { id: 28, name: 'Miguel', amount: 15000, investAmount: 300 },
  { id: 29, name: 'Jimena', amount: 18000, investAmount: 400 },
  { id: 30, name: 'Antonio', amount: 25000, investAmount: 500 },
  { id: 31, name: 'Alba', amount: 8000, investAmount: 150 },
  { id: 32, name: 'Francisco', amount: 10000, investAmount: 200 },
  { id: 33, name: 'Paula', amount: 15000, investAmount: 300 },
  { id: 34, name: 'Javier', amount: 18000, investAmount: 400 },
  { id: 35, name: 'Alejandra', amount: 25000, investAmount: 500 },
  { id: 36, name: 'Luis', amount: 8000, investAmount: 150 },
  { id: 37, name: 'María José', amount: 10000, investAmount: 200 },
  { id: 38, name: 'Marco', amount: 15000, investAmount: 300 },
  { id: 39, name: 'Inés', amount: 18000, investAmount: 400 },
  { id: 40, name: 'Pedro', amount: 25000, investAmount: 500 },
  { id: 41, name: 'Paloma', amount: 8000, investAmount: 150 },
  { id: 42, name: 'Ángel', amount: 10000, investAmount: 200 },
  { id: 43, name: 'Laura', amount: 15000, investAmount: 300 },
  { id: 44, name: 'Raúl', amount: 18000, investAmount: 400 },
  { id: 45, name: 'Rosa', amount: 25000, investAmount: 500 },
  { id: 46, name: 'Iván', amount: 8000, investAmount: 150 },
  { id: 47, name: 'Ximena', amount: 10000, investAmount: 200 },
  { id: 48, name: 'Fernando', amount: 15000, investAmount: 300 },
  { id: 49, name: 'Pilar', amount: 18000, investAmount: 400 },
  { id: 50, name: 'Joaquín', amount: 25000, investAmount: 500 },
];

const ClientSuccessPopups = () => {
  const [visiblePopups, setVisiblePopups] = useState<ClientSuccess[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const { language } = useLanguage();
  const t = translations[language];

  useEffect(() => {
    const showPopup = () => {
      const client = clientSuccesses[currentIndex];
      setVisiblePopups((prev) => [...prev, client]);

      setTimeout(() => {
        setVisiblePopups((prev) => prev.filter((p) => p.id !== client.id));
      }, 5000);

      setCurrentIndex((prev) => (prev + 1) % clientSuccesses.length);
    };

    const initialDelay = setTimeout(() => {
      showPopup();
    }, 3000);

    const interval = setInterval(showPopup, 4000);

    return () => {
      clearTimeout(initialDelay);
      clearInterval(interval);
    };
  }, [currentIndex]);

  const handleClose = (id: number) => {
    setVisiblePopups((prev) => prev.filter((p) => p.id !== id));
  };

  return (
    <div className="fixed bottom-4 right-4 z-[9999] space-y-3 max-w-sm md:max-w-sm max-w-[280px] sm:max-w-xs">
      {visiblePopups.map((popup, index) => (
        <div
          key={popup.id}
          className={`border-2 rounded-lg shadow-hover p-2.5 sm:p-3 md:p-4 animate-slide-in-right ${
            popup.isPending 
              ? 'bg-red-50 border-red-500/50' :'bg-card border-accent/20'
          }`}
          style={{
            animation: 'slideInRight 0.5s ease-out',
          }}
        >
          <div className="flex items-start space-x-2 sm:space-x-3">
            <div className={`flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center ${
              popup.isPending ? 'bg-red-100' : 'bg-accent/10'
            }`}>
              <Icon name="CheckCircleIcon" size={16} variant="solid" className={`sm:w-5 sm:h-5 md:w-6 md:h-6 ${
                popup.isPending ? 'text-red-600' : 'text-accent'
              }`} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-xs sm:text-sm font-heading font-semibold text-primary">
                    {popup.name}
                  </p>
                  <p className="text-[10px] sm:text-xs text-muted-foreground mt-0.5">
                    {t.common.invest} ${popup.investAmount}
                  </p>
                </div>
                <button
                  onClick={() => handleClose(popup.id)}
                  className="flex-shrink-0 ml-1 sm:ml-2 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label="Close notification"
                >
                  <Icon name="XMarkIcon" size={14} variant="outline" className="sm:w-4 sm:h-4" />
                </button>
              </div>
              <div className="mt-1.5 sm:mt-2 flex items-center space-x-1">
                <span className={`text-[10px] sm:text-xs ${
                  popup.isPending ? 'text-red-600 font-medium' : 'text-muted-foreground'
                }`}>
                  {popup.isPending ? t.common.pending : t.common.received}
                </span>
                <span className={`text-sm sm:text-base md:text-lg font-heading font-bold ${
                  popup.isPending ? 'text-red-600' : 'text-accent'
                }`}>
                  ${popup.amount.toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ClientSuccessPopups;
